import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  IconButton,
  Chip,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Casino as DiceIcon,
  Album as WheelIcon,
  AttachMoney as CoinIcon,
  CardGiftcard as BoxIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import api from '../services/api';

const GAME_TYPES = [
  {
    id: 'roll_dice',
    name: 'Roll the Dice',
    icon: <DiceIcon />,
    color: '#FF6B6B',
    description: '3D dice rolling game',
    defaultEntryCost: 5,
    defaultWinProbability: 45,
    defaultRewardMin: 8,
    defaultRewardMax: 20
  },
  {
    id: 'spin_wheel',
    name: 'Spin the Wheel',
    icon: <WheelIcon />,
    color: '#4ECDC4',
    description: '3D spinning wheel game (FREE)',
    defaultEntryCost: 0,
    defaultWinProbability: 15,
    defaultRewardMin: 5,
    defaultRewardMax: 30
  },
  {
    id: 'coin_flip',
    name: 'Coin Flip',
    icon: <CoinIcon />,
    color: '#FFD93D',
    description: '3D coin flipping game',
    defaultEntryCost: 3,
    defaultWinProbability: 50,
    defaultRewardMin: 5,
    defaultRewardMax: 15
  },
  {
    id: 'mystery_box',
    name: 'Mystery Box',
    icon: <BoxIcon />,
    color: '#A8E6CF',
    description: '3D mystery box reveal game',
    defaultEntryCost: 10,
    defaultWinProbability: 30,
    defaultRewardMin: 15,
    defaultRewardMax: 100
  }
];

const MEMBERSHIP_TIERS = [
  { value: 'all', label: 'All Users (Free + Paid)' },
  { value: 'free', label: 'Free Users Only' },
  { value: 'paid', label: 'Paid Users Only (Essential+)' },
  { value: 'essential', label: 'Essential Tier' },
  { value: 'professional', label: 'Professional Tier' },
  { value: 'premium', label: 'Premium Tier' }
];

interface Game {
  id: number | string;
  name: string;
  description: string;
  game_type: string;
  status: 'active' | 'inactive';
  max_plays_per_day: number;
  token_reward_min: number;
  token_reward_max: number;
  token_entry_cost: number;
  win_probability: number;
  allowed_tiers: string[];
  is_active?: boolean;
  config?: any;
}

const GamesWithTokens: React.FC = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    game_type: 'roll_dice',
    status: 'active' as 'active' | 'inactive',
    max_plays_per_day: 5,
    token_entry_cost: 0,
    token_reward_min: 10,
    token_reward_max: 50,
    win_probability: 50,
    allowed_tiers: ['all']
  });

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/games');
      const data = response.data;

      // Parse allowed_tiers and config if they are strings
      const parsedGames = (Array.isArray(data) ? data : []).map((game: any) => ({
        ...game,
        allowed_tiers: typeof game.allowed_tiers === 'string'
          ? JSON.parse(game.allowed_tiers)
          : (game.allowed_tiers || ['all']),
        config: typeof game.config === 'string'
          ? JSON.parse(game.config)
          : game.config
      }));

      setGames(parsedGames);
      setError('');
    } catch (err: any) {
      console.error('Error fetching games:', err);
      setError('Failed to load games');
      setGames([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (game?: Game) => {
    if (game) {
      console.log('Opening dialog with game:', game);
      console.log('Game ID:', game.id, 'Type:', typeof game.id);
      setSelectedGame(game);

      // Parse config if it exists
      const config = game.config ?
        (typeof game.config === 'string' ? JSON.parse(game.config) : game.config)
        : {};

      // Get game type info for defaults
      const gameTypeInfo = GAME_TYPES.find(g => g.id === game.game_type) || GAME_TYPES[0];

      setFormData({
        name: game.name || '',
        description: game.description || '',
        game_type: game.game_type || 'roll_dice',
        status: game.status || 'active',
        max_plays_per_day: game.max_plays_per_day || 5,
        token_entry_cost: game.token_entry_cost !== undefined ? game.token_entry_cost : (config.token_entry_cost !== undefined ? config.token_entry_cost : gameTypeInfo.defaultEntryCost),
        token_reward_min: game.token_reward_min || gameTypeInfo.defaultRewardMin,
        token_reward_max: game.token_reward_max || gameTypeInfo.defaultRewardMax,
        win_probability: config.win_probability !== undefined ? config.win_probability : (game.win_probability !== undefined ? game.win_probability : gameTypeInfo.defaultWinProbability),
        allowed_tiers: config.allowed_tiers || game.allowed_tiers || ['all']
      });
    } else {
      setSelectedGame(null);
      setFormData({
        name: '',
        description: '',
        game_type: 'roll_dice',
        status: 'active',
        max_plays_per_day: 5,
        token_entry_cost: 0,
        token_reward_min: 10,
        token_reward_max: 50,
        win_probability: 50,
        allowed_tiers: ['all']
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedGame(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const config = {
        win_probability: formData.win_probability,
        allowed_tiers: formData.allowed_tiers,
        token_entry_cost: formData.token_entry_cost
      };

      const now = new Date();
      const endDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

      const apiData = {
        name: formData.name,
        description: formData.description,
        game_type: formData.game_type,
        status: formData.status,
        is_active: formData.status === 'active',
        max_plays_per_day: parseInt(formData.max_plays_per_day.toString()),
        token_entry_cost: parseInt(formData.token_entry_cost.toString()),
        token_reward_min: parseInt(formData.token_reward_min.toString()),
        token_reward_max: parseInt(formData.token_reward_max.toString()),
        prize_description: `Win ${formData.token_reward_min}-${formData.token_reward_max} tokens`,
        start_date: now.toISOString().slice(0, 19).replace('T', ' '),
        end_date: endDate.toISOString().slice(0, 19).replace('T', ' '),
        config: JSON.stringify(config)
      };

      console.log('Saving game...');
      console.log('selectedGame:', selectedGame);
      console.log('selectedGame.id:', selectedGame?.id);

      // Check if we're updating an existing game (has a valid ID) or creating a new one
      if (selectedGame && selectedGame.id && selectedGame.id !== '') {
        console.log('PUT URL:', `/admin/games/${selectedGame.id}`);
        await api.put(`/admin/games/${selectedGame.id}`, apiData);
      } else {
        console.log('POST URL:', '/admin/games');
        await api.post('/admin/games', apiData);
      }

      handleCloseDialog();
      fetchGames();
    } catch (err: any) {
      console.error('Error saving game:', err);
      setError(err.message || 'Failed to save game');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (game: Game) => {
    try {
      const newStatus = game.status === 'active' ? 'inactive' : 'active';
      await api.put(`/admin/games/${game.id}`, {
        ...game,
        status: newStatus,
        is_active: newStatus === 'active'
      });
      fetchGames();
    } catch (err) {
      console.error('Error toggling game status:', err);
      setError('Failed to update game status');
    }
  };

  const getGameTypeInfo = (gameType: string) => {
    return GAME_TYPES.find(g => g.id === gameType) || GAME_TYPES[0];
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Games Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage the 4 interactive 3D games: Roll the Dice, Spin the Wheel, Coin Flip, and Mystery Box.
        Control win probabilities, token rewards, and which membership tiers can play each game.
      </Typography>

      <Grid container spacing={3}>
        {GAME_TYPES.map((gameType) => {
          const existingGame = games.find(g => g.game_type === gameType.id);
          const config = existingGame?.config ?
            (typeof existingGame.config === 'string' ? JSON.parse(existingGame.config) : existingGame.config)
            : {};

          return (
            <Grid item xs={12} sm={6} md={3} key={gameType.id}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: 2,
                      bgcolor: gameType.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: '#fff',
                      fontSize: 30
                    }}
                  >
                    {gameType.icon}
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {gameType.name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {gameType.description}
                  </Typography>

                  {existingGame ? (
                    <Stack spacing={1}>
                      <Chip
                        label={existingGame.status === 'active' ? 'Active' : 'Inactive'}
                        color={existingGame.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                      <Typography variant="caption" color="text.secondary">
                        <strong>Entry Cost:</strong> {existingGame.token_entry_cost || config.token_entry_cost || 0} tokens
                        {(existingGame.token_entry_cost === 0 || config.token_entry_cost === 0) && ' (FREE)'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Win Rate:</strong> {config.win_probability || existingGame.win_probability || 50}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Reward:</strong> {existingGame.token_reward_min}-{existingGame.token_reward_max} tokens
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Daily Plays:</strong> {existingGame.max_plays_per_day}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        <strong>Access:</strong> {config.allowed_tiers?.includes('all') ? 'All Users' : 'Paid Users'}
                      </Typography>
                    </Stack>
                  ) : (
                    <Chip label="Not Configured" color="warning" size="small" />
                  )}

                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(existingGame || {
                        id: '',
                        name: gameType.name,
                        description: gameType.description,
                        game_type: gameType.id,
                        status: 'active',
                        max_plays_per_day: 5,
                        token_entry_cost: gameType.defaultEntryCost,
                        token_reward_min: gameType.defaultRewardMin,
                        token_reward_max: gameType.defaultRewardMax,
                        win_probability: gameType.defaultWinProbability,
                        allowed_tiers: ['all']
                      } as Game)}
                    >
                      {existingGame ? <EditIcon /> : <SettingsIcon />}
                    </IconButton>
                    {existingGame && (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={existingGame.status === 'active'}
                            onChange={() => handleToggleStatus(existingGame)}
                            size="small"
                          />
                        }
                        label=""
                        sx={{ ml: 0 }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Configure Game Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedGame?.id ? 'Edit Game Settings' : 'Configure Game'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Game Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Game Type</InputLabel>
              <Select
                value={formData.game_type}
                label="Game Type"
                onChange={(e) => handleInputChange('game_type', e.target.value)}
              >
                {GAME_TYPES.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {type.icon}
                      {type.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Win Probability: {formData.win_probability}%
              </Typography>
              <Slider
                value={formData.win_probability}
                onChange={(_, value) => handleInputChange('win_probability', value)}
                min={0}
                max={100}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                  { value: 75, label: '75%' },
                  { value: 100, label: '100%' }
                ]}
                valueLabelDisplay="auto"
              />
              <Typography variant="caption" color="text.secondary">
                Percentage chance that a player wins tokens
              </Typography>
            </Box>

            <TextField
              label="Token Entry Cost"
              type="number"
              fullWidth
              value={formData.token_entry_cost}
              onChange={(e) => handleInputChange('token_entry_cost', e.target.value)}
              helperText="Cost in tokens to play this game (0 = FREE). Spin the Wheel is always free."
              InputProps={{ inputProps: { min: 0 } }}
            />

            <TextField
              label="Max Plays Per Day"
              type="number"
              fullWidth
              value={formData.max_plays_per_day}
              onChange={(e) => handleInputChange('max_plays_per_day', e.target.value)}
              helperText="Maximum number of times a user can play this game per day"
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Min Token Reward"
                  type="number"
                  fullWidth
                  value={formData.token_reward_min}
                  onChange={(e) => handleInputChange('token_reward_min', e.target.value)}
                  helperText="Minimum tokens won"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max Token Reward"
                  type="number"
                  fullWidth
                  value={formData.token_reward_max}
                  onChange={(e) => handleInputChange('token_reward_max', e.target.value)}
                  helperText="Maximum tokens won"
                />
              </Grid>
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Allowed Membership Tiers</InputLabel>
              <Select
                multiple
                value={formData.allowed_tiers}
                label="Allowed Membership Tiers"
                onChange={(e) => handleInputChange('allowed_tiers', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={MEMBERSHIP_TIERS.find(t => t.value === value)?.label}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {MEMBERSHIP_TIERS.map(tier => (
                  <MenuItem key={tier.value} value={tier.value}>
                    {tier.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Select which membership tiers can access this game
              </Typography>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name}
          >
            {selectedGame?.id ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GamesWithTokens;
